import React, { Component, ComponentType, ErrorInfo, ReactNode } from 'react';
import { captureException, withScope } from '@sentry/browser';

interface State {
  hasError: boolean;
}

// this is an attempt at a better than nothing error wrapper for react components
export default class ErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportReactBoundaryError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', margin: '1em' }}>
          Something went wrong rendering this page. Please contact support if it
          keeps happening.
        </div>
      );
    }

    return this.props.children;
  }
}

class ReactBoundaryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReactBoundaryError';
  }
}

export function reportReactBoundaryError(error: Error, errorInfo: ErrorInfo) {
  // very hacky solution to return the id from the outer function
  withScope((scope) => {
    scope.setTag('alertKey', 'React Error Boundary');
    scope.setExtras({ ...errorInfo });
    captureException(new ReactBoundaryError(error?.toString()));
  });
}

/**
 * Wrap a component with an ErrorBoundary.
 * This can be useful if you have a complicated render function
 * with no obvious place to put a top level wrapper.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```jsx
 * const MyComponent = (props) => <div>...</div>;
 *
 * export default withErrorBoundary(MyComponent);
 *
 * // resulting JSX will look like:
 * <ErrorBoundary>
 *   <div>...</div>
 * </ErrorBoundary>
 * ```
 */
export const withErrorBoundary = function <T>(Component: ComponentType<T>) {
  // eslint-disable-next-line react/display-name
  return (props: T & JSX.IntrinsicAttributes) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
};
